import { isAssumeModeFromSession } from "./tenantContext";

export interface TenantContextUrlOptions {
    /**
     * Force `&assume=1` even when the current tab is not in assume mode.
     * Used by Customer Service to ENTER assume identity from a normal tab (NG-8061).
     */
    assume?: boolean;
}

/**
 * Stamps the per-tab routing params onto `url`. A new tab never inherits sessionStorage, so the
 * tenant context has to ride on the address — App drains it back out on boot
 * (`readAndScrubTenantContextFromUrl`).
 *
 * Resolved against the ORIGIN rather than the current href so a route that forgot its leading
 * slash still lands at the root instead of nesting under whatever page happens to be open.
 * Absolute URLs pass through untouched.
 */
const toTenantContextUrl = (url: string, tenantId: number, options?: TenantContextUrlOptions): URL => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set("ctx", String(tenantId));
    if (options?.assume || isAssumeModeFromSession()) {
        target.searchParams.set("assume", "1");
    }
    return target;
};

/** Absolute form — for `window.open` / `window.location`. */
export const buildTenantContextUrl = (url: string, tenantId: number, options?: TenantContextUrlOptions): string =>
    toTenantContextUrl(url, tenantId, options).toString();

/**
 * Root-relative form — for an `<a href>`. Keeps the rendered DOM (and the browser's hover status
 * bar) showing the app's own path rather than a fully-qualified URL.
 */
export const buildTenantContextHref = (url: string, tenantId: number, options?: TenantContextUrlOptions): string => {
    const target = toTenantContextUrl(url, tenantId, options);
    return `${target.pathname}${target.search}${target.hash}`;
};
